import { z } from 'zod';

/**
 * SIL-574 (I2-D2, wayfinder-iter2-spec.md §5, §7-D2) — Custody & Audit
 * Records, Intelligence › Outcomes & Reports. A registry READ MODEL over
 * EXISTING records — no new tables, no new writers. Two record types exist in
 * the DB with real provenance today:
 *
 *   - `ownership_transfer` (asset_ownership_event, migration 525) — the
 *     append-only chain-of-custody row written by transfer_asset_ownership()
 *     when an accepted offer is finalized: original asset sold-locked, a
 *     buyer-owned clone minted, one immutable row linking them with the
 *     from/to customer_org, listing/offer, and price.
 *   - `lifecycle_event` (asset_lifecycle_event, migrations 498/603/678) — the
 *     append-only audit of lifecycle-state advances, plus (widened in 678)
 *     location and team reassignment events on an asset the caller's org owns.
 *
 * Record types deliberately OMITTED because no such table/column exists yet
 * (confirmed by schema survey, SIL-574): ITAD / sanitization certificates
 * (itad_rate is a pricing-model rate config, not a per-asset disposal
 * record — see 090_fmv_config.sql) and stored transfer signatures (no
 * signature/attestation table exists anywhere in db/schema). Per the
 * strict honest-data gate this issue calls for, this registry silently
 * omits those record types rather than rendering an empty/"coming soon"
 * section for them.
 *
 * WORDING (legal touchpoint, spec §9): this DTO and its labels deliberately
 * say "record" throughout, never "certificate" / "certified" / "verified" /
 * "assurance" / "attestation" — this registry reflects what SiliconX itself
 * recorded at write time, not a third-party certification. See the UI page's
 * own doc comment for the full flagged-wording list surfaced to legal.
 */
export const CUSTODY_RECORD_TYPES = ['ownership_transfer', 'lifecycle_event'] as const;
export type CustodyRecordType = (typeof CUSTODY_RECORD_TYPES)[number];

export const CUSTODY_RECORD_TYPE_LABELS: Record<CustodyRecordType, string> = {
  ownership_transfer: 'Ownership transfer',
  lifecycle_event: 'Lifecycle event',
};

/** Mirrors asset_lifecycle_event.event_type (migration 678) for lifecycle_event rows. */
export const CUSTODY_LIFECYCLE_EVENT_TYPES = ['lifecycle_state', 'location_change', 'team_change'] as const;
export type CustodyLifecycleEventType = (typeof CUSTODY_LIFECYCLE_EVENT_TYPES)[number];

export const CUSTODY_LIFECYCLE_EVENT_TYPE_LABELS: Record<CustodyLifecycleEventType, string> = {
  lifecycle_state: 'Lifecycle state change',
  location_change: 'Location change',
  team_change: 'Team reassignment',
};

export const custodyAuditRecordSchema = z.object({
  id: z.string().uuid(),
  recordType: z.enum(CUSTODY_RECORD_TYPES),
  occurredAt: z.string().nullable(),

  // Asset identity (both record types resolve to one asset — the original
  // for ownership_transfer, the owned asset for lifecycle_event).
  assetId: z.string().uuid().nullable(),
  serialNumber: z.string().nullable(),
  assetTag: z.string().nullable(),
  assetClassName: z.string().nullable(),
  manufacturerName: z.string().nullable(),
  skuModelName: z.string().nullable(),

  // ownership_transfer fields (null on lifecycle_event rows)
  direction: z.enum(['outgoing', 'incoming']).nullable(),
  counterpartyOrgName: z.string().nullable(),
  listingId: z.string().uuid().nullable(),
  amountUsd: z.string().nullable(),
  clonedAssetId: z.string().uuid().nullable(),

  // lifecycle_event fields (null on ownership_transfer rows)
  eventType: z.enum(CUSTODY_LIFECYCLE_EVENT_TYPES).nullable(),
  fromLabel: z.string().nullable(),
  toLabel: z.string().nullable(),
  decision: z.enum(['advanced', 'rejected']).nullable(),
});
export type CustodyAuditRecord = z.infer<typeof custodyAuditRecordSchema>;

export const custodyAuditRegistryResponseSchema = z.object({
  data: z.array(custodyAuditRecordSchema),
});
export type CustodyAuditRegistryResponse = z.infer<typeof custodyAuditRegistryResponseSchema>;
